/**
 * @file WithGMPOverview.jsx
 * @description withGMP를 상위 플랫폼으로 두고 RWS, MES, WMS, withEAI, CMMS, FEMS를 묶어 보여주는 솔루션 소개 페이지입니다.
 */

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  Activity,
  ArrowRight,
  Database,
  Factory,
  FlaskConical,
  Heart,
  Link2,
  Settings,
  ShieldCheck,
  Sparkles,
  Warehouse,
  WeightTilde,
  Zap,
} from 'lucide-react';
import { solutionBanner as heroImage, withGmpFlowBg } from '../../assets';

const SCROLL_NAV_SECTIONS = [
  { id: 'wgmp-sec-industry', label: '대상 산업' },
  { id: 'wgmp-sec-modules', label: '모듈 구성' },
  { id: 'wgmp-sec-flow', label: '운영 흐름' },
  { id: 'wgmp-sec-process', label: '구축 전략' },
  { id: 'wgmp-sec-cta', label: '도입 문의' },
];

const PLATFORM_MODULES = [
  {
    code: 'withRWS',
    name: '칭량관리',
    href: '/solution/rws.html',
    accent: '#c98a2b',
    Icon: WeightTilde,
    summary: '원료·처방·저울·바코드 검증을 연결해 칭량 오차와 기록 누락을 줄입니다.',
    tags: ['원료 칭량', 'LOT 검증', '전자기록'],
  },
  {
    code: 'withMES',
    name: '제조실행관리',
    href: '/solution/mes.html',
    accent: '#2a6fdb',
    Icon: Factory,
    summary: '작업지시, 공정 실적, 품질 기록을 제조 현장 중심으로 통합합니다.',
    tags: ['작업지시', 'eBR', '공정 실적'],
  },
  {
    code: 'withWMS',
    name: '창고물류관리',
    href: '/solution/wms.html',
    accent: '#1f8a5b',
    Icon: Warehouse,
    summary: '입고, 보관, 이동, 출고를 바코드·PDA 기반의 재고 흐름으로 관리합니다.',
    tags: ['입출고', 'FEFO', '재고 추적'],
  },
  {
    code: 'withEAI',
    name: '시스템 연계',
    href: '/solution/witheai.html',
    accent: '#b54a1f',
    Icon: Link2,
    summary: 'ERP, LIMS, 설비, 외부 시스템의 데이터를 표준 인터페이스로 연결합니다.',
    tags: ['ERP 연계', 'API', '데이터 허브'],
  },
  {
    code: 'withCMMS',
    name: '설비보전관리',
    href: '/solution/cmms.html',
    accent: '#216b52',
    Icon: Settings,
    badge: 'Coming Soon',
    summary: '예방보전, 고장 이력, 부품·비용 데이터를 설비 생애주기 관점으로 관리합니다.',
    tags: ['PM/CM', 'MTBF', '정비 이력'],
  },
  {
    code: 'withFEMS',
    name: '공장에너지관리',
    href: '/solution/fems.html',
    accent: '#2f8f6b',
    Icon: Zap,
    badge: 'Coming Soon',
    summary: '전력·가스·스팀·용수 사용량을 생산 데이터와 연결해 에너지 효율을 높입니다.',
    tags: ['에너지', '피크 관리', '탄소 리포트'],
  },
];

const FLOW_STEPS = [
  { n: '01', title: '입고·검수', module: 'WMS', text: '원자재 LOT와 보관 위치 등록' },
  { n: '02', title: '칭량·투입', module: 'RWS', text: '처방 기반 칭량과 허용오차 검증' },
  { n: '03', title: '제조·품질', module: 'MES', text: '공정 실적과 전자제조기록 생성' },
  { n: '04', title: '출고·추적', module: 'WMS', text: '제품 이동, 출하, 재고 추적' },
  { n: '05', title: '연계·운영', module: 'withEAI', text: 'ERP/LIMS/설비 데이터 동기화' },
  { n: '06', title: '최적화', module: 'CMMS·FEMS', text: '설비 안정성과 에너지 효율 개선' },
];

function SectionHead({ eyebrow, title, sub }) {
  return (
    <div className="wgmp-section-head">
      <p className="wgmp-eyebrow">{eyebrow}</p>
      <h2 className="wgmp-section-title">{title}</h2>
      {sub && <p className="wgmp-section-sub">{sub}</p>}
    </div>
  );
}

function ScrollNav() {
  const navRef = useRef(null);
  const [active, setActive] = useState(SCROLL_NAV_SECTIONS[0].id);
  /* 어두운 배경(hero·CTA) 위에 있을 때만 흰색으로 전환합니다. */
  const [isOverDark, setIsOverDark] = useState(false);

  useLayoutEffect(() => {
    const nav = navRef.current;
    if (!nav) return undefined;

    let frameId = 0;
    const update = () => {
      cancelAnimationFrame(frameId);
      frameId = requestAnimationFrame(() => {
        const navRect = nav.getBoundingClientRect();
        const over = ['.wgmp-hero', '.wgmp-cta'].some((sel) => {
          const section = document.querySelector(sel);
          if (!section) return false;
          const sRect = section.getBoundingClientRect();
          return navRect.top < sRect.bottom && navRect.bottom > sRect.top;
        });
        setIsOverDark((prev) => (prev === over ? prev : over));
      });
    };

    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    window.addEventListener('load', update);
    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
      window.removeEventListener('load', update);
    };
  }, []);

  useEffect(() => {
    let frameId = 0;

    const updateActive = () => {
      cancelAnimationFrame(frameId);
      frameId = requestAnimationFrame(() => {
        const activationLine = window.innerHeight * 0.42;
        const current = SCROLL_NAV_SECTIONS.reduce((matchedId, { id }) => {
          const el = document.getElementById(id);
          if (!el) return matchedId;
          return el.getBoundingClientRect().top <= activationLine ? id : matchedId;
        }, SCROLL_NAV_SECTIONS[0].id);

        setActive((prev) => (prev === current ? prev : current));
      });
    };

    updateActive();
    window.addEventListener('scroll', updateActive, { passive: true });
    window.addEventListener('resize', updateActive);
    window.addEventListener('load', updateActive);
    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener('scroll', updateActive);
      window.removeEventListener('resize', updateActive);
      window.removeEventListener('load', updateActive);
    };
  }, []);

  const goTo = (id) => {
    const el = document.getElementById(id);
    if (el) window.scrollTo({ top: el.offsetTop - 40, behavior: 'smooth' });
  };

  return (
    <nav ref={navRef} className={`scroll-nav${isOverDark ? ' is-over-hero' : ''}`} aria-label="withGMP section navigation">
      <ul>
        {SCROLL_NAV_SECTIONS.map(({ id, label }) => (
          <li key={id} className={active === id ? 'is-active' : ''}>
            <button type="button" onClick={() => goTo(id)} aria-current={active === id ? 'true' : undefined}>
              <span className="sn-label">{label}</span>
              <span className="sn-marker" aria-hidden="true" />
            </button>
          </li>
        ))}
      </ul>
    </nav>
  );
}

export function WithGMPHero({ onNavigate }) {
  const trustItems = [
    { value: '21년', label: 'GMP IT 전문 경험', sub: '2005~' },
    { value: '6개', label: '운영 모듈', sub: 'RWS · MES · WMS +' },
    { value: '10년+', label: '장기 유지보수', sub: '고객사 다수' },
    { value: 'KGMP', label: 'cGMP / EU-GMP', sub: '레퍼런스' },
  ];

  return (
    <section className="wgmp-hero">
      <img className="wgmp-hero-image" src={heroImage} alt="" aria-hidden="true" />
      <div className="wgmp-hero-vignette" aria-hidden="true" />

      <div className="container">
        <div className="wgmp-hero-inner">
          <div className="wgmp-hero-badge">
            <span className="wgmp-hero-badge-dot" aria-hidden="true" />
            Smart GMP Factory · AI Powered
          </div>

          <h1 className="wgmp-hero-title">
            제조 현장의 모든 흐름을
            <br />
            <span className="wgmp-hero-title-accent">하나의 시스템</span>으로
          </h1>

          <p className="wgmp-hero-desc">
            새힘정보기술의 <strong>withGMP</strong>는 RWS · MES · WMS를 중심으로
            withEAI · CMMS · FEMS까지 확장되는 GMP 전문 통합 플랫폼입니다.
          </p>

          <div className="wgmp-hero-actions">
            <button
              type="button"
              className="wgmp-btn wgmp-btn-primary"
              onClick={() => onNavigate('/contact')}
            >
              도입 상담 요청 <ArrowRight size={16} />
            </button>
          </div>

          <div className="wgmp-trust-strip">
            {trustItems.map((item) => (
              <div key={item.value} className="wgmp-trust-item">
                <span className="wgmp-trust-value">{item.value}</span>
                <span className="wgmp-trust-label">{item.label}</span>
                <span className="wgmp-trust-sub">{item.sub}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export function WithGMPIndustry() {
  const industries = [
    {
      Icon: FlaskConical,
      title: '제약',
      desc: '원료·중간재·완제품 LOT 추적과 cGMP / EU-GMP 적합 전자기록 관리',
    },
    {
      Icon: Heart,
      title: '건강기능식품',
      desc: '원재료 입고부터 완제품 출하까지 표준화된 GMP 프로세스 구축',
    },
    {
      Icon: Sparkles,
      title: '화장품',
      desc: '처방 기반 칭량과 배치 단위 품질관리로 ISO 22716 대응',
    },
  ];

  return (
    <section className="wgmp-industry">
      <div className="container">
        <SectionHead
          eyebrow="TARGET INDUSTRY"
          title="GMP 산업 전반에 검증된 전문성"
          sub="제약·화장품·건강기능식품 등 GMP 규제 산업에 특화된 솔루션을 제공합니다"
        />
        <div className="wgmp-industry-grid">
          {industries.map((ind) => (
            <article key={ind.title} className="wgmp-industry-card">
              <div className="wgmp-industry-icon">
                <ind.Icon size={28} />
              </div>
              <h3 className="wgmp-industry-title">{ind.title}</h3>
              <p className="wgmp-industry-desc">{ind.desc}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

const MODULE_FLOW_META = [
  { code: 'withWMS', desc: '입고·보관·출고까지 정확한 재고와 물류 관리' },
  { code: 'withRWS', desc: '정확한 칭량과 자재 투입으로 신뢰할 수 있는 생산의 시작' },
  { code: 'withMES', desc: '실시간 생산 실행과 품질 관리로 생산성 및 추적성 강화' },
  { code: 'withEAI', desc: '내부 시스템과 외부 서비스의 안정적인 연계 및 데이터 통합' },
  { code: 'withCMMS', desc: '설비 점검·보전 이력 관리로 설비 가동률과 신뢰성 향상' },
  { code: 'withFEMS', desc: '에너지 사용 모니터링과 분석으로 효율 개선 및 비용 절감' },
];

const MODULE_FLOW_BENEFITS = [
  { Icon: Database, title: '단일 데이터 기반', desc: '정확한 의사결정' },
  { Icon: ShieldCheck, title: '컴플라이언스 대응', desc: '감사 추적성 강화' },
  { Icon: Activity, title: '전사 흐름 가시화', desc: '운영 효율 극대화' },
  { Icon: Sparkles, title: '지속 가능한 제조', desc: 'ESG 가치 실현' },
];

function ModuleFlowNode({ module, desc, onNavigate }) {
  const Icon = module.Icon;

  const handleNavigate = () => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    onNavigate(module.href);
  };

  return (
    <article className="wgmp-flow-module" style={{ '--module-accent': module.accent }}>
      <span className="wgmp-flow-module-drop" aria-hidden="true" />
      <button
        type="button"
        className="wgmp-flow-module-btn"
        onClick={handleNavigate}
        aria-label={`${module.code} ${module.name} 상세 보기`}
      >
        <span className="wgmp-flow-module-icon">
          <Icon size={21} strokeWidth={1.85} />
        </span>
        <strong>{module.code}</strong>
        <ArrowRight className="wgmp-flow-module-arrow" size={14} strokeWidth={2.2} aria-hidden="true" />
      </button>
      <p>{desc}</p>
    </article>
  );
}

function ModulePortfolio({ onNavigate }) {
  const modulesByCode = PLATFORM_MODULES.reduce((acc, module) => {
    acc[module.code] = module;
    return acc;
  }, {});

  return (
    <section className="wgmp-mock-modules wgmp-flow-section">
      <div className="container">
        <div className="wgmp-flow-stage">
          <img className="wgmp-flow-bg" src={withGmpFlowBg} alt="" aria-hidden="true" />

          <div className="wgmp-flow-copy">
            <p className="wgmp-mock-eyebrow">WITHGMP MODULES</p>
            <h2>제조 현장의 흐름을 하나로 연결</h2>
            <p>
              물류·칭량부터 생산, 시스템 연계, 설비 관리, 에너지 관리까지
              여섯 개의 모듈이 하나의 플랫폼 <strong>withGMP</strong>로 통합되어
              데이터가 끊기지 않는 스마트 제조 환경을 완성합니다.
            </p>
          </div>

          <div className="wgmp-flow-backbone" aria-hidden="true">
            <span className="wgmp-flow-backbone-grid" />
            <strong>withGMP</strong>
          </div>

          <div className="wgmp-flow-modules" aria-label="withGMP 모듈 상세 페이지">
            {MODULE_FLOW_META.map((item) => (
              <ModuleFlowNode
                key={item.code}
                module={modulesByCode[item.code]}
                desc={item.desc}
                onNavigate={onNavigate}
              />
            ))}
          </div>

          <div className="wgmp-flow-benefits">
            {MODULE_FLOW_BENEFITS.map(({ Icon, title, desc }) => (
              <div key={title} className="wgmp-flow-benefit">
                <span>
                  <Icon size={26} strokeWidth={1.7} />
                </span>
                <strong>{title}</strong>
                <small>{desc}</small>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function OperatingFlow() {
  return (
    <section className="wgmp-mock-flow">
      <div className="container">
        <div className="wgmp-mock-flow-inner">
          <div className="wgmp-mock-flow-copy">
            <p className="wgmp-mock-eyebrow">OPERATING FLOW</p>
            <h2>공정 흐름과 운영 데이터를 끊지 않는 구성</h2>
            <p>
              withGMP의 강점은 현장의 실제 운영 순서에 맞춰 데이터가 어디서 생성되고 어디로
              확장되는지 설명할 때 더 선명해집니다.
            </p>
            <div className="wgmp-mock-flow-points">
              <span><ShieldCheck size={17} /> GMP 기록성과 추적성 확보</span>
              <span><Database size={17} /> 마스터 데이터와 실적 데이터 통합</span>
              <span><Activity size={17} /> 설비·에너지까지 운영 지표 확장</span>
            </div>
          </div>

          <div className="wgmp-mock-flow-board">
            {FLOW_STEPS.map((step) => (
              <article key={step.n} className="wgmp-mock-flow-step">
                <span>{step.n}</span>
                <div>
                  <strong>{step.title}</strong>
                  <small>{step.module}</small>
                  <p>{step.text}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export function WithGMPProcess() {
  const steps = [
    { n: '01', t: '현황 분석', d: '기존 운영 프로세스 진단 및 GMP 요구사항 도출' },
    { n: '02', t: '표준 모델 설계', d: '표준 프로세스 수립' },
    { n: '03', t: '단계적 구축', d: '솔루션 적용 → 검증 → 안정화' },
    { n: '04', t: '다공장 확산', d: '추가 공장 동일 모델 적용' },
    { n: '05', t: '지속 운영', d: '장기 유지보수 및 기능 고도화' },
  ];

  return (
    <section className="wgmp-process">
      <div className="container">
        <SectionHead eyebrow="DELIVERY APPROACH" title="단계적이고 안정적인 구축 전략" />
        <div className="wgmp-process-steps">
          <div className="wgmp-process-line" aria-hidden="true" />
          {steps.map((s) => (
            <div key={s.n} className="wgmp-process-step">
              <div className="wgmp-process-num">{s.n}</div>
              <h3 className="wgmp-process-step-title">{s.t}</h3>
              <p className="wgmp-process-step-desc">{s.d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function WithGMPCTA({ onNavigate }) {
  return (
    <section className="wgmp-cta">
      <svg
        aria-hidden="true"
        className="wgmp-cta-bg"
        viewBox="0 0 1600 400"
        preserveAspectRatio="xMidYMid slice"
      >
        <defs>
          <pattern id="wgmp-cta-grid" width="40" height="40" patternUnits="userSpaceOnUse">
            <path d="M40 0H0V40" fill="none" stroke="#fff" strokeWidth="0.5" />
          </pattern>
        </defs>
        <rect width="1600" height="400" fill="url(#wgmp-cta-grid)" />
        <path
          d="M-50 240 C 320 180, 540 320, 880 260 S 1340 220, 1700 260"
          fill="none"
          stroke="#fff"
          strokeOpacity="0.4"
          strokeWidth="2"
        />
      </svg>

      <div className="container">
        <div className="wgmp-cta-inner">
          <h2 className="wgmp-cta-title">
            제조 현장의 디지털 전환,
            <br />
            withGMP가 함께합니다
          </h2>
          <p className="wgmp-cta-desc">
            ㈜새힘정보기술이 21년간 축적한 GMP IT 노하우로 귀사의 스마트팩토리를 책임지고 구축합니다.
          </p>
          <div className="wgmp-cta-actions">
            <button
              type="button"
              className="wgmp-cta-btn wgmp-cta-btn-primary"
              onClick={() => onNavigate('/contact')}
            >
              도입 문의 <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function WithGMPOverview({ onNavigate }) {
  return (
    <>
      <ScrollNav />
      <WithGMPHero onNavigate={onNavigate} />
      <div id="wgmp-sec-industry">
        <WithGMPIndustry />
      </div>
      <div id="wgmp-sec-modules">
        <ModulePortfolio onNavigate={onNavigate} />
      </div>
      <div id="wgmp-sec-flow">
        <OperatingFlow />
      </div>
      <div id="wgmp-sec-process">
        <WithGMPProcess />
      </div>
      <div id="wgmp-sec-cta">
        <WithGMPCTA onNavigate={onNavigate} />
      </div>
    </>
  );
}
